// Voice-Kanalansicht (Hauptbereich) und Statusleiste (unten in der Kanalliste).
import { useEffect } from 'react';
import { Link } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { getVoiceController } from './controller';
import { LeaveButtonCompact, MuteDeafButtons, VoiceControls } from './controls';
import { SpeakerIcon } from './icons';
import { qualityLabel, shortUserLabel } from './logic';
import { installVoiceDebugHook } from './debug-hook';
import { useVoiceStore } from './store';
import { Avatar, ParticipantTile, QualityBars, ScreenTile } from './tiles';

export interface VoiceChannelViewProps {
  serverId: string;
  channelId: string;
}

installVoiceDebugHook();

/** Abfrageintervall der VoiceStates des geöffneten Kanals. */
const STATES_POLL_MS = 10_000;

function Lobby({ serverId, channelId }: VoiceChannelViewProps) {
  const status = useVoiceStore((s) => s.status);
  const activeChannel = useVoiceStore((s) => s.channelId);
  const states = useVoiceStore(useShallow((s) => s.channelStates[channelId] ?? []));
  const users = useVoiceStore((s) => s.users);
  const connecting = status === 'connecting' && activeChannel === channelId;
  const elsewhere = status !== 'idle' && activeChannel !== null && activeChannel !== channelId;

  return (
    <div className="lf-voice-lobby">
      <div className="lf-voice-lobby__ember" aria-hidden="true" />
      {states.length > 0 ? (
        <ul className="lf-voice-lobby__people" aria-label="Bereits im Kanal">
          {states.map((st) => {
            const name = users[st.userId]?.name ?? shortUserLabel(st.userId);
            return (
              <li key={st.userId} data-testid={`voice-preview-${st.userId}`} title={name}>
                <Avatar name={name} url={users[st.userId]?.avatarUrl ?? null} size="sm" />
                <span>{name}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="lf-voice-hint">Noch niemand da – entfache das Gespräch.</p>
      )}
      <button
        type="button"
        className="lf-voice-join"
        data-testid="voice-join"
        disabled={connecting}
        aria-busy={connecting}
        onClick={() => {
          void getVoiceController().join(serverId, channelId);
        }}
      >
        {connecting ? 'Verbinde …' : elsewhere ? 'Hierher wechseln' : 'Beitreten'}
      </button>
    </div>
  );
}

function Stage() {
  const participants = useVoiceStore((s) => s.participants);
  const screens = participants.filter((p) => p.hasScreen && p.screenTrack);
  return (
    <div className={`lf-voice-stage${screens.length > 0 ? ' has-screens' : ''}`}>
      {screens.length > 0 ? (
        <div className="lf-voice-screens">
          {screens.map((p) => (
            <ScreenTile key={`screen-${p.userId}`} p={p} />
          ))}
        </div>
      ) : null}
      <div className="lf-voice-grid" data-count={participants.length} role="list" aria-label="Teilnehmer">
        {participants.map((p) => (
          <ParticipantTile key={p.userId} p={p} />
        ))}
      </div>
    </div>
  );
}

function Notices() {
  const status = useVoiceStore((s) => s.status);
  const error = useVoiceStore((s) => s.error);
  const canPlayback = useVoiceStore((s) => s.canPlaybackAudio);
  const serverMute = useVoiceStore((s) => s.serverMute);
  const serverDeaf = useVoiceStore((s) => s.serverDeaf);
  const moderated = status !== 'idle' && (serverMute || serverDeaf);
  return (
    <>
      {moderated ? (
        <div className="lf-voice-notice lf-voice-notice--warn" role="status" data-testid="voice-server-moderation">
          {serverMute && serverDeaf
            ? 'Ein Moderator hat dich stumm- und taubgeschaltet.'
            : serverDeaf
              ? 'Ein Moderator hat dich taubgeschaltet.'
              : 'Ein Moderator hat dich stummgeschaltet.'}
        </div>
      ) : null}
      {status === 'reconnecting' ? (
        <div className="lf-voice-notice lf-voice-notice--warn" role="status" data-testid="voice-reconnecting">
          Verbindung wird wiederhergestellt …
        </div>
      ) : null}
      {!canPlayback && status !== 'idle' ? (
        <div className="lf-voice-notice" role="status">
          Der Browser hat die Wiedergabe blockiert.
          <button type="button" className="lf-voice-linkbtn" data-testid="voice-start-audio" onClick={() => void getVoiceController().startAudio()}>
            Audio aktivieren
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="lf-voice-notice lf-voice-notice--error" role="alert" data-testid="voice-error">
          {error}
          <button type="button" className="lf-voice-linkbtn" onClick={() => getVoiceController().clearError()} aria-label="Hinweis schließen">
            OK
          </button>
        </div>
      ) : null}
    </>
  );
}

export function VoiceChannelView({ serverId, channelId }: VoiceChannelViewProps) {
  const status = useVoiceStore((s) => s.status);
  const activeChannel = useVoiceStore((s) => s.channelId);
  const name = useVoiceStore((s) => s.channelNames[channelId]);
  const quality = useVoiceStore((s) => s.quality);
  const count = useVoiceStore((s) => s.participants.length);
  const limit = useVoiceStore((s) => s.session?.userLimit ?? 0);
  const here = activeChannel === channelId && (status === 'connected' || status === 'reconnecting');

  useEffect(() => {
    const ctrl = getVoiceController();
    void ctrl.loadChannelName(channelId);
    // Zustände (wer ist drin, wer ist taub) – ergänzt bzw. ersetzt Socket-Updates, falls die Shell keine anbindet.
    void ctrl.loadChannelStates(channelId);
    const t = window.setInterval(() => void ctrl.loadChannelStates(channelId), STATES_POLL_MS);
    return () => window.clearInterval(t);
  }, [channelId]);

  return (
    <section className="lf-voice" data-testid="voice-channel-view" data-channel-id={channelId} data-voice-status={here ? status : 'idle'} aria-label="Sprachkanal">
      <header className="lf-voice-header">
        <SpeakerIcon />
        <h2>{name ?? 'Sprachkanal'}</h2>
        {here ? (
          <>
            <span className="lf-voice-count" title="Teilnehmer">
              {limit > 0 ? `${count}/${limit}` : count}
            </span>
            <QualityBars level={quality} testId="voice-connection-quality" />
            <span className="lf-voice-hint">{qualityLabel(quality)}</span>
          </>
        ) : null}
      </header>
      <Notices />
      {here ? (
        <>
          <Stage />
          <VoiceControls />
        </>
      ) : (
        <Lobby serverId={serverId} channelId={channelId} />
      )}
    </section>
  );
}

export function VoiceStatusBar() {
  const status = useVoiceStore((s) => s.status);
  const serverId = useVoiceStore((s) => s.serverId);
  const channelId = useVoiceStore((s) => s.channelId);
  const name = useVoiceStore((s) => (s.channelId ? s.channelNames[s.channelId] : undefined));
  const quality = useVoiceStore((s) => s.quality);
  if (status === 'idle' || !channelId) return null;
  const label = status === 'connecting' ? 'Verbinde …' : status === 'reconnecting' ? 'Verbindung wird wiederhergestellt …' : 'Sprachverbindung steht';
  const to = serverId ? `/channels/${serverId}/${channelId}` : `/channels/@me/${channelId}`;
  return (
    <div className="lf-voice-status" data-testid="voice-status-bar" data-voice-status={status} role="region" aria-label="Sprachverbindung">
      <div className="lf-voice-status__info">
        <span className={`lf-voice-status__state lf-voice-status__state--${status}`}>
          {status === 'connected' ? <QualityBars level={quality} testId="voice-status-quality" /> : null}
          {label}
        </span>
        <Link to={to} className="lf-voice-status__channel" data-testid="voice-status-channel">
          {name ?? 'Sprachkanal'}
        </Link>
      </div>
      <div className="lf-voice-status__actions">
        <MuteDeafButtons prefix="voice-status" compact />
        <LeaveButtonCompact />
      </div>
    </div>
  );
}
